/**
 * InteractionsTable Component
 *
 * Renders a single list of node interactions (either negative/control-ineffective
 * or positive/control-effective) as a searchable, sortable, paginated table.
 *
 * Search and sort run entirely client-side over the in-memory report list.
 * Each instance owns its own search/sort/pagination state, so the two tabs in
 * NodeInteractionsPanel filter and sort independently.
 */

import React, {useCallback, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {IrpMappedReportDTO} from '../../../../../../@types/integrated-risk-picture';
import {useIRPContext} from '../IRPWorkbookProvider';

/** Sortable column keys. */
type SortColumn = 'reportId' | 'date' | 'source';
type SortDirection = 'asc' | 'desc';

type PageSize = 10 | 25 | 50;

/** Lazily-fetched full report content for the Report ID hover preview. */
interface ReportPreviewState {
    loading: boolean;
    content?: string;
    error?: string;
}

interface InteractionsTableProps {
    /** The reports to display (already split into one interaction polarity). */
    reports: IrpMappedReportDTO[];
    /** Message shown when this list has no rows (before any search is applied). */
    emptyMessage: string;
    /** Called with the numeric report id when the view-report action is clicked. */
    onViewReport: (reportId: number) => void;
    /** Called when "Export All" is clicked. Exports the full panel dataset, not this filtered view. */
    onExportAll: () => void;
    /** Total report count across both tabs, used for the "Export all N reports" label/title. */
    totalReportCount: number;
}

/**
 * Format date for display (MM/DD/YYYY format)
 */
const formatDate = (dateValue: string | Date | undefined): string => {
    if (!dateValue) return 'N/A';
    try {
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    } catch {
        return 'N/A';
    }
};

/** Parse a date string to epoch millis, or null when missing/unparseable. */
const parseTime = (dateValue: string | null | undefined): number | null => {
    if (!dateValue) return null;
    const time = new Date(dateValue).getTime();
    return isNaN(time) ? null : time;
};

/** Some report bodies arrive with escaped newlines (literal "\n"); render them as real breaks. */
const unescapeNewlines = (text: string): string =>
    text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\r/g, '\n');

const InteractionsTable: React.FC<InteractionsTableProps> = ({
    reports,
    emptyMessage,
    onViewReport,
    onExportAll,
    totalReportCount
}) => {
    const {dataSource} = useIRPContext();

    // Lazily-fetched full report content for the Report ID hover preview, keyed by reportId.
    const [reportPreviews, setReportPreviews] = useState<Record<number, ReportPreviewState>>({});
    const previewStartedRef = useRef<Set<number>>(new Set());

    // Fetch the full report when the tooltip opens (after its 750ms enter delay).
    // Guarded so each report is fetched at most once across re-hovers and both tabs.
    const handleReportPreviewOpen = useCallback((reportId: number): void => {
        if (!dataSource || previewStartedRef.current.has(reportId)) return;
        previewStartedRef.current.add(reportId);
        setReportPreviews((prev) => ({ ...prev, [reportId]: { loading: true } }));
        dataSource.getWorkbookReportTab(reportId)
            .then((data) => setReportPreviews((prev) => ({
                ...prev,
                [reportId]: { loading: false, content: data.content }
            })))
            .catch((err: any) => setReportPreviews((prev) => ({
                ...prev,
                [reportId]: { loading: false, error: err?.message || 'Failed to load report' }
            })));
    }, [dataSource]);

    // Tooltip body for a Report ID: loading spinner, error, or the full report content.
    const renderReportPreview = (reportId: number): React.ReactNode => {
        const state = reportPreviews[reportId];
        if (!state || state.loading) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <CircularProgress size={12} color="inherit" />
                    <Typography sx={{ fontSize: '0.7rem' }}>Loading report...</Typography>
                </Box>
            );
        }
        if (state.error) {
            return <Typography sx={{ fontSize: '0.7rem' }}>{state.error}</Typography>;
        }
        return (
            <Typography sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                {state.content ? unescapeNewlines(state.content) : 'No content available'}
            </Typography>
        );
    };

    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState<PageSize>(25);

    // Filter by free-text search across id, source, justification and excerpt.
    const filteredReports = useMemo((): IrpMappedReportDTO[] => {
        const query = search.trim().toLowerCase();
        if (!query) return reports;
        return reports.filter((r) =>
            (r.uniqueId || '').toLowerCase().includes(query) ||
            (r.sourceName || '').toLowerCase().includes(query) ||
            (r.justification || '').toLowerCase().includes(query) ||
            (r.excerpt || '').toLowerCase().includes(query)
        );
    }, [reports, search]);

    // Sort the filtered rows. Rows with no parseable date always sort last,
    // regardless of direction. A null sortColumn preserves the original order.
    const sortedReports = useMemo((): IrpMappedReportDTO[] => {
        if (!sortColumn) return filteredReports;
        const dir = sortDirection === 'asc' ? 1 : -1;
        const sorted = [...filteredReports];
        sorted.sort((a, b) => {
            if (sortColumn === 'date') {
                const ta = parseTime(a.dateTime);
                const tb = parseTime(b.dateTime);
                if (ta === null && tb === null) return 0;
                if (ta === null) return 1;
                if (tb === null) return -1;
                return (ta - tb) * dir;
            }
            if (sortColumn === 'source') {
                return (a.sourceName || '').localeCompare(b.sourceName || '', undefined, { sensitivity: 'base' }) * dir;
            }
            // reportId: order by the displayed uniqueId, tie-break on numeric id
            const cmp = (a.uniqueId || '').localeCompare(b.uniqueId || '', undefined, { numeric: true, sensitivity: 'base' });
            return (cmp !== 0 ? cmp : a.reportId - b.reportId) * dir;
        });
        return sorted;
    }, [filteredReports, sortColumn, sortDirection]);

    // Pagination derived from the sorted set. Clamp the page so a shrinking
    // result set (e.g. after typing a search) never lands on an empty page.
    const total = sortedReports.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const startIndex = safePage * pageSize;
    const endIndex = startIndex + pageSize;
    const displayed = sortedReports.slice(startIndex, endIndex);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearch(event.target.value);
        setPage(0);
    }, []);

    const handleClearSearch = useCallback((): void => {
        setSearch('');
        setPage(0);
    }, []);

    const handleSort = useCallback((column: SortColumn): void => {
        setSortColumn((prevColumn) => {
            if (prevColumn === column) {
                setSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
                return prevColumn;
            }
            setSortDirection('asc');
            return column;
        });
    }, []);

    const handlePrevious = useCallback((): void => {
        setPage((prev) => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback((): void => {
        setPage((prev) => Math.min(totalPages - 1, prev + 1));
    }, [totalPages]);

    const handlePageSizeChange = useCallback((event: SelectChangeEvent<number>): void => {
        setPageSize(event.target.value as PageSize);
        setPage(0);
    }, []);

    const renderSortableHeader = (column: SortColumn, label: string, width: string): React.ReactElement => (
        <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'medium', width, whiteSpace: 'nowrap' }} sortDirection={sortColumn === column ? sortDirection : false}>
            <TableSortLabel
                active={sortColumn === column}
                direction={sortColumn === column ? sortDirection : 'asc'}
                onClick={(): void => handleSort(column)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search toolbar */}
            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by report ID, source or justification..."
                    value={search}
                    onChange={handleSearchChange}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.75rem' } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16 }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch} title="Clear search" sx={{ padding: '2px' }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined
                    }}
                />
            </Box>

            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {renderSortableHeader('reportId', 'Report ID', '14%')}
                            {renderSortableHeader('date', 'Date', '9%')}
                            {renderSortableHeader('source', 'Source', '9%')}
                            <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'medium', width: '60%' }}>
                                Justification
                            </TableCell>
                            <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'medium', width: '8%' }} align="center">
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayed.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        {search.trim() ? 'No reports match your search' : emptyMessage}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            displayed.map((interaction) => (
                                <TableRow
                                    key={interaction.reportId}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        <Tooltip
                                            title={renderReportPreview(interaction.reportId)}
                                            placement="right"
                                            arrow
                                            enterDelay={750}
                                            leaveDelay={200}
                                            onOpen={(): void => handleReportPreviewOpen(interaction.reportId)}
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        maxWidth: 700,
                                                        maxHeight: 500,
                                                        overflow: 'auto'
                                                    }
                                                }
                                            }}
                                        >
                                            <span style={{ cursor: 'pointer' }}>{interaction.uniqueId}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                        {formatDate(interaction.dateTime)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                        {interaction.sourceName || 'Unknown'}
                                    </TableCell>
                                    <TableCell>
                                        {interaction.justification ? (
                                            <Tooltip
                                                title={
                                                    <Typography sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                                        {interaction.justification}
                                                    </Typography>
                                                }
                                                placement="top"
                                                enterDelay={500}
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            maxWidth: 500
                                                        }
                                                    }
                                                }}
                                            >
                                                <Typography sx={{
                                                    fontSize: '0.75rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    lineHeight: 1.3,
                                                    cursor: 'pointer'
                                                }}>
                                                    {interaction.justification}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                                No justification
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={(): void => onViewReport(interaction.reportId)}
                                            sx={{ padding: '2px' }}
                                            title="View Report Details"
                                        >
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1, px: 1 }}
            >
                {/* Left side - pagination info */}
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {total === 0
                        ? 'Showing 0 of 0'
                        : `Showing ${startIndex + 1}-${Math.min(endIndex, total)} of ${total}`}
                </Typography>

                {/* Right side - controls */}
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Export All button */}
                    {totalReportCount > 0 && (
                        <Button
                            onClick={onExportAll}
                            size="small"
                            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                            sx={{ fontSize: '0.7rem', py: 0.25 }}
                            title={`Export all ${totalReportCount} reports to CSV`}
                        >
                            Export All
                        </Button>
                    )}

                    {/* Page size selector */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Per page:
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    '& .MuiSelect-select': {
                                        py: 0.25,
                                        px: 1
                                    }
                                }}
                            >
                                <MenuItem value={10} sx={{ fontSize: '0.7rem' }}>10</MenuItem>
                                <MenuItem value={25} sx={{ fontSize: '0.7rem' }}>25</MenuItem>
                                <MenuItem value={50} sx={{ fontSize: '0.7rem' }}>50</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    {/* Page navigation */}
                    {totalPages > 1 && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Button
                                onClick={handlePrevious}
                                size="small"
                                disabled={safePage === 0}
                                sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 'auto', px: 1 }}
                            >
                                Previous
                            </Button>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', px: 1 }}>
                                Page {safePage + 1} of {totalPages}
                            </Typography>
                            <Button
                                onClick={handleNext}
                                size="small"
                                disabled={safePage >= totalPages - 1}
                                sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 'auto', px: 1 }}
                            >
                                Next
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default React.memo(InteractionsTable);
